import React, { useState, useEffect } from "react"
import { SortNewsByPublishedYear } from "../news-helper"

export default function NewsOverviewBlockComponent({
  news,
  newsCategories,
  header,
  subtext,
  fallbackImage,
  readLessButtonText,
  readMoreButtonText,
  selectYearButtonText,
  allNewsCategoriesText,
}) {
  //#region tabhandling

  const tabLabels = newsCategories || []

  const convertUtfToReadableString = (url) => {
    return (
      (!!url &&
        url
          .replaceAll("%20", " ")
          .replaceAll("%C3%A6", "æ")
          .replaceAll("%C3%86", "Æ")
          .replaceAll("%C3%B8", "ø")
          .replaceAll("%C3%98", "Ø")
          .replaceAll("%C3%A5", "å")
          .replaceAll("%C3%85", "Å")) ||
      url
    )
  }

  const [urlHash] = useState(convertUtfToReadableString(window.location.href.split("#")[1]) || undefined)
  const [matchingTabLabel] = useState(tabLabels.find((tabLabel) => convertUtfToReadableString(tabLabel) === urlHash) || undefined)
  const [activeTab, setActiveTab] = useState(matchingTabLabel || "")

  if (!activeTab) {
    setActiveTab(allNewsCategoriesText)
  }

  useEffect(() => {
    const handleLocationChange = () => {
      setActiveTab(convertUtfToReadableString(window.location.href.split("#")[1] || "") || tabLabels[0])
    }

    window.addEventListener("popstate", handleLocationChange)

    return () => {
      window.removeEventListener("popstate", handleLocationChange)
    }
  }, [])

  //#endregion

  const [uniqueYears, setUniqueYears] = useState([])
  const [selectedYear, setSelectedYear] = useState("")
  const [isReadingMore, setIsReadingMore] = useState(false)

  //resultsets dependant in other states
  const newsFilteredByPageCategories = news.filter(
    (news) =>
      news &&
      news.NewsCategoryFriendlyNames_ss &&
      news.NewsCategoryFriendlyNames_ss.some((NewsCategoryFriendlyNames_ss) => newsCategories.includes(NewsCategoryFriendlyNames_ss)),
  )

  const newsSortedByYear = SortNewsByPublishedYear(newsFilteredByPageCategories)

  //First we filter news sets based on year, then we filter news by desired category,
  //this way Tabs can distinguish what categories are present for a given year
  const [newsFilteredByYear, setNewsFilteredByYear] = useState(newsSortedByYear)
  const [newsFilteredByCategoryYear, setNewsFilteredByCategoryYear] = useState(newsSortedByYear)

  //news item results that are rendered based on desired news categories / year resultset.
  const [newsItemsShortListToRender, setNewsItemsShortListToRender] = useState([])
  const [newsItemsAllToRender, setNewsItemsAllToRender] = useState([])

  useEffect(() => {
    const uniqueYearsSet = new Set()

    newsSortedByYear.forEach((newsItem) => {
      if (newsItem.Year_s) {
        uniqueYearsSet.add(newsItem.Year_s)
      }
    })
    setUniqueYears(Array.from(uniqueYearsSet).sort((a, b) => b - a))
  }, [news])

  //The filtering engine of the news overview page.
  useEffect(() => {
    let filteredNews
    let filteredNewsByYear

    if (!selectedYear && activeTab === allNewsCategoriesText) {
      //setting news to all news entries, possibly filter away categories in news that arent chosen on pageCategories.
      setNewsFilteredByYear(newsSortedByYear)
      setNewsFilteredByCategoryYear(newsSortedByYear)
      return
    }

    if (!selectedYear && activeTab !== allNewsCategoriesText) {
      filteredNews = newsSortedByYear.filter(
        (news) =>
          news &&
          news.NewsCategoryFriendlyNames_ss &&
          news.NewsCategoryFriendlyNames_ss.some((NewsCategoryFriendlyNames_ss) => NewsCategoryFriendlyNames_ss.includes(activeTab)),
      )
      //setting news based on a given category, taking all years into account
      setNewsFilteredByCategoryYear(filteredNews)
      setNewsFilteredByYear(newsSortedByYear)

      return
    }

    if (selectedYear && activeTab === allNewsCategoriesText) {
      filteredNews = newsSortedByYear && newsSortedByYear.filter((news) => news && news.Year_s === selectedYear)
      //selecting news based on year and all categories
      setNewsFilteredByCategoryYear(filteredNews)
      setNewsFilteredByYear(filteredNews)
      return
    }

    if (selectedYear && activeTab !== allNewsCategoriesText) {
      filteredNews =
        newsSortedByYear &&
        newsSortedByYear.filter(
          (news) =>
            news &&
            news.NewsCategoryFriendlyNames_ss &&
            news.NewsCategoryFriendlyNames_ss.some((NewsCategoryFriendlyNames_ss) => activeTab.includes(NewsCategoryFriendlyNames_ss)) &&
            (selectedYear === null || news.Year_s === selectedYear),
        )
      filteredNewsByYear = newsSortedByYear && newsSortedByYear.filter((news) => news && news.Year_s === selectedYear)

      //setting news based on a chosen year and a chosen category
      setNewsFilteredByYear(filteredNewsByYear)
      setNewsFilteredByCategoryYear(filteredNews)
      return
    }
  }, [selectedYear, activeTab])

  useEffect(() => {
    let newsItems =
      newsFilteredByCategoryYear &&
      newsFilteredByCategoryYear.map(
        (item) =>
          item && (
            <NewsCard
              key={item.Header_s + " " + item.OriginalPublishDate_s}
              img={item.ImageUrl_s || fallbackImage}
              url={item.PageUrl_s}
              heading={item.Header_s}
              publishDate={item.OriginalPublishDate_s.split(" ")[0]}
            />
          ),
      )

    // Use Set to keep track of unique elements
    const uniqueElementsSet = new Set()

    // Filter the JSX elements array to remove duplicates
    const uniqueJsxElements =
      newsItems &&
      newsItems.filter((element) => {
        const elementId = element && element.key
        if (!uniqueElementsSet.has(elementId)) {
          uniqueElementsSet.add(elementId)
          return true
        }
        return false
      })

    setNewsItemsShortListToRender(uniqueJsxElements && uniqueJsxElements.slice(0, 9))
    setNewsItemsAllToRender(uniqueJsxElements)
  }, [newsFilteredByCategoryYear, isReadingMore])

  const htmlTabs =
    tabLabels.length > 0
      ? tabLabels.map((tab, index) => {
          const hasNewsInTabCategory = true
          return (
            <Tab
              key={tab ? tab.replace(/\s+/g, "-") : index}
              label={tab}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              hasNewsInTabCategory={hasNewsInTabCategory}
              newsCategories={newsCategories}
              newsFilteredByCategoryYear={newsFilteredByYear}
              isAllCategories={false}
            />
          )
        })
      : undefined

  const handleSelectYear = (year) => {
    var firstButton = document.querySelector(".news-tabs button:first-child")

    if (firstButton) {
      var buttonId = firstButton.id
      setActiveTab(buttonId)
      setSelectedYear(year)

      const newUrl = window.location.href.split("#")[0] + "#" + buttonId
      window.history.pushState({ urlChange: "TabNavigation" }, "", newUrl)
    }
  }

  //markup
  return (
    <section className="news-overview-section">
      {/**heading area */}
      <h1 className="overview-heading w--normal" data-epi-property-name="Header">
        {header}
      </h1>
      <p className="overview-subtext w--narrow" data-epi-property-name="Subtext">
        {subtext}
      </p>
      {/**heading area end*/}

      {/**tabs area */}
      <div className="news-tabs">
        {/**all categories tab first, then added categories*/}
        <Tab
          key={allNewsCategoriesText ? allNewsCategoriesText && allNewsCategoriesText.replace(/\s+/g, "-") : 0}
          label={allNewsCategoriesText}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          newsCategories={newsCategories}
          newsFilteredByCategoryYear={newsFilteredByYear}
          isAllCategories={true}
        />
        {htmlTabs}

        {/**news year selector is last */}
        <div className="yearselect-container">
          <div className="firefox-fix">
            <label data-epi-property-name="SelectYearButtonText" htmlFor="year">
              {selectYearButtonText}
            </label>
            <select id="year" onChange={(e) => handleSelectYear(e.target.value)} value={selectedYear}>
              <option value="">{selectYearButtonText}</option>
              {uniqueYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/**tabs area end*/}

      {/**news generation*/}
      <div className="news-content grid align-left w--normal">{isReadingMore ? newsItemsAllToRender : newsItemsShortListToRender}</div>
      {/**news generation end*/}
      <div className="readmore-button-container">
        {newsFilteredByCategoryYear && newsFilteredByCategoryYear.length > 9 ? (
          <button className={isReadingMore ? "readless" : "readmore"} onClick={() => setIsReadingMore(!isReadingMore)}>
            {isReadingMore ? readLessButtonText : readMoreButtonText}
          </button>
        ) : undefined}
      </div>
    </section>
  )
}

function Tab({ label, activeTab, setActiveTab, newsFilteredByCategoryYear, isAllCategories }) {
  let tabHasNews =
    newsFilteredByCategoryYear &&
    newsFilteredByCategoryYear.some(
      (newsFilteredByCategoryYear) => newsFilteredByCategoryYear && newsFilteredByCategoryYear.NewsCategoryFriendlyNames_ss?.includes(label),
    )

  const tabHandler = () => {
    if (tabHasNews || isAllCategories) {
      if (activeTab !== label) setActiveTab(label)

      const newUrl = window.location.href.split("#")[0] + "#" + label
      window.history.pushState({ urlChange: "TabNavigation" }, "", newUrl)
    }
  }
  return (
    <button
      id={label}
      aria-pressed={label === activeTab ? "true" : "false"}
      onClick={tabHandler}
      //if tab is all categories, never disable, otherwise disable if tab doesnt have news.
      disabled={isAllCategories ? false : !tabHasNews ? true : false}
      className={`${label === activeTab ? "is-active" : "inactive"} ${isAllCategories || tabHasNews ? "tab-vibrant" : "tab-faded"}`}
    >
      {label}
    </button>
  )
}

const NewsCard = ({ img, url, heading, publishDate }) => {
  return (
    <div className="grid-item col--12 col-xs--6 col-md--4">
      <a href={url} target="_blank" rel="noopener noreferrer" className="news-card">
        <img src={img} /*alt={heading}*/ className="news-card-image" />
        <div className="news-card-content">
          <p className="news-card-date">{publishDate.replaceAll("-", ".").replaceAll("/", ".")}</p>
          <h2 className="news-card-heading">{heading}</h2>
        </div>
      </a>
    </div>
  )
}
